import type { ErrorEvent } from "@sentry/nextjs";

/**
 * Error messages caused by the visitor's environment, not by our code.
 * Every entry is backed by events seen in production:
 * - "Java object is gone": Android WebView bridge of the Instagram/Facebook in-app browser
 * - "Failed to connect to MetaMask": wallet browser extension
 * - "Load failed" / "Failed to fetch" / "NetworkError": aborted requests (navigation, offline, content blockers)
 */
export const IGNORED_ERROR_PATTERNS: (string | RegExp)[] = [
  /Java object is gone/,
  /Failed to connect to MetaMask/,
  /^(TypeError: )?Load failed$/,
  /^(TypeError: )?Failed to fetch$/,
  /NetworkError when attempting to fetch resource/,
];

/**
 * Scripts whose frames we never want to count as application errors:
 * browser extensions and the script the Facebook/Instagram Android in-app
 * browser injects into every page.
 */
export const DENIED_URL_PATTERNS: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari(-web)?-extension:\/\//i,
  /navigation_performance_logger_android/i,
];

const NEXT_BUNDLE_MARKER = "/_next/";

/**
 * Drops "Maximum call stack size exceeded" events whose stack contains no frame
 * from our Next.js bundles. Those originate from scripts injected into the page
 * (e.g. Chrome iOS translate), where Sentry reports the page URL as filename.
 */
export const dropThirdPartyStackOverflow = (
  event: ErrorEvent
): ErrorEvent | null => {
  const exceptions = event.exception?.values ?? [];
  const isStackOverflow = exceptions.some(
    (exception) =>
      exception.type === "RangeError" &&
      /Maximum call stack size exceeded/i.test(exception.value ?? "")
  );

  if (!isStackOverflow) {
    return event;
  }

  const frames = exceptions.flatMap(
    (exception) => exception.stacktrace?.frames ?? []
  );
  const hasAppFrame = frames.some((frame) =>
    (frame.filename ?? "").includes(NEXT_BUNDLE_MARKER)
  );

  return hasAppFrame ? event : null;
};
